import React, { useState, useEffect } from 'react';
import useScrollLock from '../../hooks/useScrollLock';
import './VideoTranscodingModal.css';

const VideoTranscodingModal = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  progress = 0, 
  status = 'preparing',
  fileName = '',
  error = null 
}) => {
  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (status === 'processing' || status === 'preparing') {
      // Don't allow closing while processing
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  const getStatusText = () => {
    switch (status) {
      case 'preparing':
        return 'Preparing video for transcoding... (will be trimmed to 30 seconds)';
      case 'processing':
        return 'Transcoding video...';
      case 'completed':
        return 'Video transcoding completed!';
      case 'error':
        return 'Transcoding failed';
      case 'cancelled':
        return 'Transcoding cancelled';
      default:
        return 'Processing video...';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'preparing':
        return '⏳';
      case 'processing':
        return '🔄';
      case 'completed':
        return '✅';
      case 'error':
        return '❌';
      case 'cancelled':
        return '⏹️';
      default:
        return '🔄';
    }
  };

  const canCancel = status === 'preparing' || status === 'processing';
  const canClose = status === 'completed' || status === 'error' || status === 'cancelled';

  return (
    <div className={`video-transcoding-modal-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`video-transcoding-modal ${isClosing ? 'closing' : ''}`}>
        <div className="video-transcoding-modal-header">
          <h3>Video Transcoding</h3>
          {canClose && (
            <button 
              className="video-transcoding-modal-close"
              onClick={handleClose}
              aria-label="Close modal"
            >
              ×
            </button>
          )}
        </div>

        <div className="video-transcoding-modal-content">
          <div className="video-transcoding-status">
            <div className="video-transcoding-icon">
              {getStatusIcon()}
            </div>
            <div className="video-transcoding-text">
              <h4>{getStatusText()}</h4>
              {fileName && (
                <p className="video-transcoding-filename">{fileName}</p>
              )}
            </div>
          </div>

          {(status === 'preparing' || status === 'processing') && (
            <div className="video-transcoding-progress">
              <div className="video-transcoding-progress-bar">
                <div 
                  className="video-transcoding-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="video-transcoding-progress-text">
                {progress}%
              </div>
            </div>
          )}

          {status === 'completed' && (
            <div className="video-transcoding-success">
              <p>Your video has been successfully transcoded and optimized!</p>
              <p><small>Note: Videos longer than 30 seconds have been trimmed to the first 30 seconds.</small></p>
            </div>
          )}

          {status === 'error' && error && (
            <div className="video-transcoding-error">
              <p><strong>Error:</strong> {error}</p>
              <p>Please try uploading a different video file.</p>
            </div>
          )}

          {status === 'cancelled' && (
            <div className="video-transcoding-cancelled">
              <p>Video transcoding was cancelled.</p>
            </div>
          )}
        </div>

        <div className="video-transcoding-modal-footer">
          {canCancel && (
            <button 
              className="video-transcoding-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
          )}
          {canClose && (
            <button 
              className="video-transcoding-close-btn"
              onClick={handleClose}
            >
              {status === 'completed' ? 'Continue' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoTranscodingModal;
